// A batch's sandbox (LH-F5): opening it on the agent with the store's copy of the code as the seed, the lender's
// variables as .env, the signed-in person as the git author and Settings as the container limits; and the `invoke`
// the canvas, the test call and the test runner use to run one endpoint in it. On the API (LH-B2) the API takes the
// seed from the batch's git branch and the values from the vault; what is sent here beyond the batch is ignored.
import { getAgent, sandboxId, type AgentClient, type OpenRequest, type SandboxInfo } from '@/lib/agent'
import { apiMode } from '@/lib/hubApi'
import { whenSynced } from '@/store/hubStore'
import { testsPathOf } from '@/lib/endpoints'
import { codeKeyOf, testsJson } from '@/lib/batch'
import type { Invoke } from '@/lib/flowEngine'
import type { Batch, Lender, PortalSettings, User } from '@/types'

/** What a single test hands the handler as ctx.prev (the earlier step's outcome): a lender reference for status / offers endpoints. */
export const DEMO_PREV: Record<string, unknown> = { outcome: 'ok', reply_code: 'LEAD_CREATED', lender_ref: 'DEMO-9001' }

/** The variables with a value for this environment. LENDER_BASE_URL always follows Lender info. */
export function envFor(lender: Lender, environment: 'uat' | 'production'): Record<string, string> {
  const out: Record<string, string> = {}
  for (const v of lender.envVars) {
    const value = environment === 'uat' ? v.uat : v.prod
    if (value !== null && value !== '' && !value.startsWith('•')) out[v.name] = value
  }
  const base = environment === 'uat' ? lender.env.uatBaseUrl : lender.env.prodBaseUrl
  if (base) out.LENDER_BASE_URL = base
  return out
}

/**
 * The variables the sandbox's .env gets. On the API (LH-B2) the API writes .env itself from the vault (UAT for testers,
 * production for admins, every read audited) and the browser sends none: it only sees secrets as "•••".
 */
export async function sandboxEnv(lender: Lender, environment: 'uat' | 'production'): Promise<Record<string, string>> {
  return apiMode() ? {} : envFor(lender, environment)
}

/** The seed the sandbox starts from: the store's files plus the generated tests/*.replies.json. */
export function seedFiles(batch: Batch) {
  const tests = batch.manifest.endpoints.filter((e) => batch.tests.some((t) => t.endpoint === e.path)).map((e) => ({ path: testsPathOf(e.path), content: testsJson(batch.tests, e.path) }))
  return [...batch.files, ...tests]
}

export interface OpenOptions {
  user: Pick<User, 'name' | 'email'>
  settings: PortalSettings
  environment?: 'uat' | 'production'
  reset?: boolean
  agent?: AgentClient
}

export async function openSandbox(lender: Lender, batch: Batch, opts: OpenOptions): Promise<SandboxInfo> {
  const agent = opts.agent ?? getAgent()
  // on the API the batch may still be on its way (Create API, New version): the Hub must have it before its sandbox
  if (apiMode()) await whenSynced()
  const environment = opts.environment ?? 'uat'
  const req: OpenRequest = {
    lender: codeKeyOf(batch),
    version: batch.version,
    files: seedFiles(batch),
    env: await sandboxEnv(lender, environment),
    environment,
    author: { name: opts.user.name, email: opts.user.email },
    limits: { memoryMb: opts.settings.runner.memoryMb },
    reset: opts.reset,
    seed: batch.git.commit,
  }
  return agent.open(req)
}

/** Runs one endpoint of the batch in its sandbox (opened first with openSandbox). */
export function invokeFor(_lender: Lender, batch: Batch, environment: 'uat' | 'production' = 'uat', agent: AgentClient = getAgent()): Invoke {
  const sb = sandboxId(codeKeyOf(batch), batch.version)
  return (endpoint, input, prev, reply) => agent.run(sb, { file: endpoint.file, input, prev, reply, mode: reply ? 'sample' : 'real', environment, timeoutMs: 30000 })
}
